import { createContext, useContext, useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * BillingContext — single source of truth for the current user's
 * subscription/trial/comp entitlement. Refreshed:
 *  - on login (when AuthContext.user flips truthy)
 *  - explicitly via `reload()` (e.g. after successful checkout)
 *
 * Server-side gating is the real enforcer; this context just powers
 * UI affordances (paywall cards, "X days left" banner, etc.).
 */
const BillingContext = createContext(null);

const EMPTY = {
  tier: "expired",
  status: "expired",
  can_voter_list: false,
  can_paid: false,
  trial_days_left: 0,
  trial_ends_at: null,
  current_period_end: null,
  cancel_at_period_end: false,
  interval: "monthly",
  is_owner: false,
  is_comp: false,
  comp_code: null,
  stripe_configured: false,
  publishable_key_present: false,
  plans: {
    standard: { monthly: { price_usd: 34.99, configured: false }, yearly: { price_usd: 377.89, configured: false } },
    pro: { monthly: { price_usd: 49.99, configured: false }, yearly: { price_usd: 539.89, configured: false } },
  },
  loaded: false,
};

export function BillingProvider({ children }) {
  const { user } = useAuth();
  const [ent, setEnt] = useState(EMPTY);

  const reload = useCallback(async () => {
    if (!user) {
      setEnt(EMPTY);
      return;
    }
    try {
      const { data } = await api.get("/billing/status");
      setEnt({ ...data, loaded: true });
    } catch {
      setEnt({ ...EMPTY, loaded: true });
    }
  }, [user]);

  useEffect(() => {
    reload();
  }, [reload]);

  const value = useMemo(() => ({ ...ent, reload }), [ent, reload]);
  return <BillingContext.Provider value={value}>{children}</BillingContext.Provider>;
}

export const useBilling = () => useContext(BillingContext) || EMPTY;
